import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Scale, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState, DataTag, Disclaimer } from "@/components/common";
import { LEGISLATION_TYPES } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Legislation() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [tipo, setTipo] = useState("");
    const [ano, setAno] = useState("");

    const load = () => {
        const params = {};
        if (q) params.q = q;
        if (tipo) params.tipo = tipo;
        if (ano) params.ano = ano;
        api.get("/legislation", { params }).then((r) => setItems(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []); // eslint-disable-line
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, tipo, ano]); // eslint-disable-line

    const years = [...new Set(items.map((i) => i.year))].filter(Boolean);

    return (
        <div className="space-y-6" data-testid="legislation-page">
            <PageHeader title="Legislação Mineral" subtitle="Normas oficiais verificáveis, com fonte e link direto." />

            <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input data-testid="legislation-search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Pesquisar legislação, assunto ou palavra-chave..."
                        className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-petroleum/20 focus:border-petroleum transition-colors" />
                </div>
                <div className="flex flex-wrap gap-3">
                    <Select value={tipo || "all"} onValueChange={(v) => setTipo(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-56" data-testid="legislation-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Todos os tipos</SelectItem>{LEGISLATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={ano || "all"} onValueChange={(v) => setAno(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-40" data-testid="legislation-filter-year"><SelectValue placeholder="Ano" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Todos os anos</SelectItem>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </div>

            {items.length === 0 ? (
                <EmptyState icon={Scale} title="Nenhuma norma encontrada" desc="Ajuste os filtros ou o termo de busca." />
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {items.map((l) => (
                        <div key={l.id} onClick={() => navigate(`/legislacao/${l.id}`)} data-testid={`legislation-card-${l.id}`}
                            className="bg-white border border-slate-200 rounded-md p-5 shadow-sm hover:shadow-md hover:border-petroleum/30 transition-all cursor-pointer">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-petroleum/10 text-petroleum border border-petroleum/20 font-semibold">{l.type}</span>
                                <DataTag kind="oficial" />
                            </div>
                            <h3 className="mt-3 font-heading font-bold text-slate-900 leading-snug">{l.title}</h3>
                            <p className="mt-2 text-sm text-slate-600 line-clamp-3">{l.summary}</p>
                            <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                                <span>{l.theme} · {l.year}</span>
                                <a href={l.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-petroleum hover:text-petroleumHover"><ExternalLink className="w-3 h-3" /> Fonte oficial</a>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <Disclaimer>Base composta por normas oficiais verificáveis. Confirme sempre a redação vigente diretamente na fonte oficial.</Disclaimer>
        </div>
    );
}
